import { useState, useEffect, useCallback } from "react";
import {
  _get_admin_dashboard_stats,
  _get_admin_interventions_count,
  _get_admin_users,
  _verify_admin_user,
  _get_admin_transactions,
  _get_admin_wallets,
  _get_admin_logs,
  _get_admin_orders_dashboard,
  _get_admin_orders,
  _get_admin_interventions_dashboard,
  _get_admin_interventions,
  _get_admin_order_detail,
  _get_admin_intervention_detail,
  _add_admin_business_service,
  _get_admin_config_raw,
  _update_admin_config_raw,
  _delete_admin_config_raw,
  _unhide_admin_config_raw,
} from "../../../common/axios_services";

const useAdminPortalData = () => {
  const [dashboardStats, setDashboardStats] = useState({});
  const [interventionCounts, setInterventionCounts] = useState({});
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [logs, setLogs] = useState([]);
  const [ordersDashboard, setOrdersDashboard] = useState({});
  const [orders, setOrders] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  const [interventionsDashboard, setInterventionsDashboard] = useState({});
  const [interventions, setInterventions] = useState([]);
  const [interventionDetail, setInterventionDetail] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, countsRes] = await Promise.all([
        _get_admin_dashboard_stats(),
        _get_admin_interventions_count(),
      ]);
      setDashboardStats(statsRes?.data?.data || {});
      setInterventionCounts(countsRes?.data?.data || {});
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load dashboard stats");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1, limit = 20, search = "") => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (search) params.search = search;
      const res = await _get_admin_users(page, limit);
      setUsers(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load users");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(
    async (page = 1, limit = 20, filters = {}) => {
      try {
        setLoading(true);
        const res = await _get_admin_transactions(page, limit, filters);
        setTransactions(res?.data?.data);
        return { success: true, data: res?.data?.data };
      } catch (err) {
        setError(err.message || "Failed to load transactions");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchWallets = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      const res = await _get_admin_wallets(page, limit);
      setWallets(res?.data?.data);
      return { success: true, data: res?.data };
    } catch (err) {
      setError(err.message || "Failed to load wallets");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async (page = 1, limit = 20, filters = {}) => {
    try {
      setLoading(true);
      const res = await _get_admin_logs(page, limit, filters);
      setLogs(res?.data?.data);
      return { success: true, data: res?.data };
    } catch (err) {
      setError(err.message || "Failed to load logs");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrdersDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await _get_admin_orders_dashboard();
      setOrdersDashboard(res?.data?.data || {});
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load orders dashboard");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(
    async (type = "todays", page = 1, limit = 20) => {
      try {
        setLoading(true);
        const res = await _get_admin_orders(type, page, limit);
        setOrders(res?.data?.data);
        return { success: true, data: res?.data?.data };
      } catch (err) {
        setError(err.message || "Failed to load orders");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchInterventionsDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await _get_admin_interventions_dashboard();
      setInterventionsDashboard(res?.data?.data || {});
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load interventions dashboard");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterventions = useCallback(async (type, page = 1, limit = 20) => {
    try {
      setLoading(true);
      const res = await _get_admin_interventions(type, page, limit);
      setInterventions(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load interventions");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await _get_admin_config_raw();
      setConfig(res?.data?.data || {});
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to load config");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfig = useCallback(
    async (configData) => {
      try {
        setLoading(true);
        const res = await _update_admin_config_raw(configData);
        await fetchConfig();
        return { success: true };
      } catch (err) {
        setError(err.message || "Failed to update config");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [fetchConfig],
  );

  const deleteConfig = useCallback(
    async (type) => {
      try {
        setLoading(true);
        await _delete_admin_config_raw(type);
        await fetchConfig();
        return { success: true };
      } catch (err) {
        setError(err.message || "Failed to hide config");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [fetchConfig],
  );

  const unhideConfig = useCallback(
    async (type) => {
      try {
        setLoading(true);
        await _unhide_admin_config_raw(type);
        await fetchConfig();
        return { success: true };
      } catch (err) {
        setError(err.message || "Failed to unhide config");
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [fetchConfig],
  );

  const verifyUser = useCallback(
    async (userId) => {
      try {
        await _verify_admin_user(userId);
        await fetchUsers();
        return { success: true };
      } catch (err) {
        setError(err.message || "Failed to verify user");
        return { success: false };
      }
    },
    [fetchUsers],
  );

  const addBusinessService = useCallback(async (businessId, serviceData) => {
    try {
      await _add_admin_business_service(businessId, serviceData);
      return { success: true };
    } catch (err) {
      setError(err.message || "Failed to add service");
      return { success: false };
    }
  }, []);

  const fetchOrderDetail = useCallback(async (orderId) => {
    try {
      setLoading(true);
      const res = await _get_admin_order_detail(orderId);
      setOrderDetail(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load order detail");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInterventionDetail = useCallback(async (interventionId) => {
    try {
      setLoading(true);
      const res = await _get_admin_intervention_detail(interventionId);
      setInterventionDetail(res?.data?.data);
      return { success: true, data: res?.data?.data };
    } catch (err) {
      setError(err.message || "Failed to load intervention detail");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    dashboardStats,
    interventionCounts,
    users,
    transactions,
    wallets,
    logs,
    ordersDashboard,
    orders,
    orderDetail,
    interventionsDashboard,
    interventions,
    interventionDetail,
    config,
    loading,
    error,
    fetchDashboardStats,
    fetchUsers,
    fetchTransactions,
    fetchWallets,
    fetchLogs,
    fetchOrdersDashboard,
    fetchOrders,
    fetchOrderDetail,
    fetchInterventionsDashboard,
    fetchInterventions,
    fetchInterventionDetail,
    fetchConfig,
    updateConfig,
    deleteConfig,
    unhideConfig,
    verifyUser,
    addBusinessService,
  };
};

export default useAdminPortalData;
